/**
 * usePagination — hook condiviso per la paginazione di TUTTI gli
 * elenchi del gestionale (Registro Pratiche, Clienti, Fatture,
 * Diario, Lavoro Quotidiano, ecc.).
 *
 * Spec utente (20/02/2026):
 *   "Tutti gli elenchi del gestionale quando raggiungono 50 righe
 *    creano nuova pagina + pulsante di scelta pagina (1,2,3,…) +
 *    selettore righe per pagina (10/30/50/100)."
 *
 * Design:
 *   - Default 50 righe per pagina (allineato a quanto richiesto).
 *   - Pulsanti pagina + selettore visualizzati da `<PaginationBar>`
 *     (vedi `components/ui/pagination-bar.jsx`).
 *   - `pageSize` persistito in localStorage per `storageKey` (così
 *     l'operatore ritrova la propria preferenza). `page` NON è
 *     persistito: si torna sempre a pagina 1 al reload.
 *   - Quando l'array filtrato cambia (per filtri / search / sort /
 *     CRUD), riportiamo `page` a 1 SOLO se la pagina corrente è
 *     ora oltre l'ultima — così l'utente che elimina una riga
 *     dalla pagina 3 non viene catapultato a pagina 1.
 *   - Restituisce `paged` già "slice"-ato pronto per `.map(...)`.
 *
 * Esempio d'uso:
 *   const { paged, paginationBar } = usePagination(filtered, {
 *     storageKey: "registro-pratiche",
 *   });
 *   return (<><Table>{paged.map(row => …)}</Table>{paginationBar}</>);
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { PaginationBar } from "@/components/ui/pagination-bar";

const DEFAULT_PAGE_SIZES = [10, 30, 50, 100];
const DEFAULT_PAGE_SIZE = 50;
const STORAGE_PREFIX = "pagination-prefs:";

const readStoredPageSize = (storageKey, fallback) => {
  if (!storageKey || typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + storageKey);
    const n = Number.parseInt(raw, 10);
    if (Number.isFinite(n) && n > 0) return n;
  } catch {
    /* localStorage non disponibile (es. private mode) — silently ignore */
  }
  return fallback;
};

const writeStoredPageSize = (storageKey, value) => {
  if (!storageKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + storageKey, String(value));
  } catch {
    /* idem */
  }
};

export function usePagination(items, options = {}) {
  const {
    defaultPageSize = DEFAULT_PAGE_SIZE,
    pageSizes = DEFAULT_PAGE_SIZES,
    storageKey = null,
    /** Etichetta singolare per il footer (es. "fattura"). */
    itemLabelSingular = "voce",
    /** Etichetta plurale (es. "fatture"). */
    itemLabelPlural = "voci",
    /** Nasconde il selector quando il totale è ≤ del page-size minimo. */
    autoHideWhenSmall = false,
  } = options;

  const safeItems = Array.isArray(items) ? items : [];
  const total = safeItems.length;

  const [pageSize, _setPageSize] = useState(() =>
    readStoredPageSize(storageKey, defaultPageSize)
  );
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Clamp `page` quando i dati cambiano: se l'utente ha eliminato
  // l'ultima riga della pagina 3 e ora ci sono solo 2 pagine,
  // portiamo silenziosamente l'utente all'ultima pagina disponibile.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const setPageSize = useCallback(
    (next) => {
      const n = Number.parseInt(next, 10);
      if (!Number.isFinite(n) || n <= 0) return;
      _setPageSize(n);
      writeStoredPageSize(storageKey, n);
      // Tornare a pagina 1 quando si cambia il pageSize è il
      // comportamento atteso (es. da 50 → 100: pagina corrente
      // potrebbe non avere più senso). Più prevedibile.
      setPage(1);
    },
    [storageKey]
  );

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return safeItems.slice(start, start + pageSize);
  }, [safeItems, page, pageSize]);

  // Range visibile (1-based, "X – Y di Z").
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(total, page * pageSize);

  const paginationBar = (
    <PaginationBar
      page={page}
      pageSize={pageSize}
      pageSizes={pageSizes}
      total={total}
      totalPages={totalPages}
      rangeStart={rangeStart}
      rangeEnd={rangeEnd}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
      itemLabelSingular={itemLabelSingular}
      itemLabelPlural={itemLabelPlural}
      autoHide={autoHideWhenSmall && total <= Math.min(...pageSizes)}
    />
  );

  return {
    paged,
    page,
    setPage,
    pageSize,
    setPageSize,
    total,
    totalPages,
    rangeStart,
    rangeEnd,
    paginationBar,
  };
}
